/**
 * AriaCustomize — Tab "Personalizza".
 * Persona name, business_profile, custom_instructions (tono, priorità commerciali).
 */
import React, { useEffect, useState } from "react";
import { Save, Sparkles, Loader } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAriaCustomer } from "./AriaCustomerArea";

const TONES = [
    { value: "professionale", label: "Professionale" },
    { value: "amichevole", label: "Amichevole" },
    { value: "diretto", label: "Diretto" },
    { value: "premium", label: "Premium / Luxury" },
];

export default function AriaCustomize() {
    const { activeToken, config, refreshConfig } = useAriaCustomer();
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!config) return;
        setForm({
            persona_name: config.persona_name || "",
            business_profile: {
                category: config.business_profile?.category || "",
                target_audience: config.business_profile?.target_audience || "",
                unique_value_proposition: config.business_profile?.unique_value_proposition || "",
                price_range: config.business_profile?.price_range || "medio",
                phone: config.business_profile?.phone || "",
            },
            custom_instructions: {
                tone_of_voice: config.custom_instructions?.tone_of_voice || "professionale",
                operating_area: config.custom_instructions?.operating_area || "",
                commercial_priorities: config.custom_instructions?.commercial_priorities || "",
            },
        });
    }, [config]);

    const save = async () => {
        if (!form || !activeToken) return;
        setSaving(true); setError(""); setSaved(false);
        try {
            await api.patch(`/widget/${activeToken}`, form);
            await refreshConfig();
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e) { setError(formatApiError(e)); }
        setSaving(false);
    };

    if (!form) return <div className="text-white/45 text-[12.5px]">Caricamento…</div>;

    const upd = (path, value) => {
        setForm((f) => {
            const next = { ...f };
            const keys = path.split(".");
            let ref = next;
            for (let i = 0; i < keys.length - 1; i++) { ref[keys[i]] = { ...ref[keys[i]] }; ref = ref[keys[i]]; }
            ref[keys[keys.length - 1]] = value;
            return next;
        });
    };

    return (
        <div className="max-w-3xl" data-testid="tab-customize">
            <h1 className="text-white text-[24px] mb-2" style={{ fontWeight: 300, letterSpacing: "-0.02em" }}>Personalizza Aria</h1>
            <p className="text-white/65 text-[14px] mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>
                Dai personalità e identità alla tua Aria. Più precisi questi campi, più Aria risponde con le tue parole, non con quelle generiche.
            </p>

            <Section title="Identità">
                <Field label="Nome Aria" hint="Come la tua Aria si presenta in chat. Es: 'Aria di Pizzeria Bella'">
                    <input
                        type="text"
                        value={form.persona_name}
                        onChange={(e) => upd("persona_name", e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg bg-white/[0.04] text-white text-[14px] border border-white/[0.08] focus:border-white/30 outline-none transition"
                        placeholder="Aria"
                        data-testid="input-persona-name"
                    />
                </Field>
                <Field label="Categoria del business" hint="Es: ristorante, tipografia, palestra, ecommerce…">
                    <input
                        type="text"
                        value={form.business_profile.category}
                        onChange={(e) => upd("business_profile.category", e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg bg-white/[0.04] text-white text-[14px] border border-white/[0.08] focus:border-white/30 outline-none"
                        placeholder="ristorante"
                        data-testid="input-category"
                    />
                </Field>
                <Field label="Target clienti" hint="A chi ti rivolgi? Es: 'famiglie 30-55, residenti zona Centro'">
                    <input
                        type="text"
                        value={form.business_profile.target_audience}
                        onChange={(e) => upd("business_profile.target_audience", e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg bg-white/[0.04] text-white text-[14px] border border-white/[0.08] focus:border-white/30 outline-none"
                        placeholder="famiglie 30-55 zona Centro"
                        data-testid="input-target"
                    />
                </Field>
                <Field label="Cosa ti rende unico" hint="L'unica frase che, sentita, fa dire al cliente 'voglio comprare qui'">
                    <textarea
                        value={form.business_profile.unique_value_proposition}
                        onChange={(e) => upd("business_profile.unique_value_proposition", e.target.value)}
                        rows={2}
                        className="w-full px-4 py-2.5 rounded-lg bg-white/[0.04] text-white text-[14px] border border-white/[0.08] focus:border-white/30 outline-none resize-none"
                        placeholder="Tipografia con consegna in 24h su tutta Italia"
                        data-testid="input-uvp"
                    />
                </Field>
            </Section>

            <Section title="Stile conversazionale">
                <Field label="Tono di voce">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {TONES.map((t) => (
                            <button key={t.value} type="button"
                                onClick={() => upd("custom_instructions.tone_of_voice", t.value)}
                                className="px-3 py-2.5 rounded-lg text-[12.5px] text-center transition"
                                style={{
                                    background: form.custom_instructions.tone_of_voice === t.value ? "linear-gradient(135deg,#A855F7,#7C3AED)" : "rgba(255,255,255,0.04)",
                                    color: form.custom_instructions.tone_of_voice === t.value ? "#fff" : "rgba(255,255,255,0.75)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    fontWeight: form.custom_instructions.tone_of_voice === t.value ? 500 : 400,
                                }}
                                data-testid={`tone-${t.value}`}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                </Field>
                <Field label="Zona operativa" hint="Dove servi i clienti. Es: 'Milano e hinterland'">
                    <input
                        type="text"
                        value={form.custom_instructions.operating_area}
                        onChange={(e) => upd("custom_instructions.operating_area", e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg bg-white/[0.04] text-white text-[14px] border border-white/[0.08] focus:border-white/30 outline-none"
                        placeholder="Milano e hinterland"
                        data-testid="input-area"
                    />
                </Field>
                <Field label="Priorità commerciali" hint="Cosa Aria deve spingere? Quali prodotti/servizi? Cosa evitare?">
                    <textarea
                        value={form.custom_instructions.commercial_priorities}
                        onChange={(e) => upd("custom_instructions.commercial_priorities", e.target.value)}
                        rows={3}
                        className="w-full px-4 py-2.5 rounded-lg bg-white/[0.04] text-white text-[14px] border border-white/[0.08] focus:border-white/30 outline-none resize-none"
                        placeholder="Spingere il menù degustazione. Mai parlare di consegna a domicilio."
                        data-testid="input-priorities"
                    />
                </Field>
            </Section>

            <Section title="Contatti">
                <Field label="Telefono titolare" hint="Numero a cui Aria può girare richieste urgenti. Formato +39…">
                    <input
                        type="tel"
                        value={form.business_profile.phone}
                        onChange={(e) => upd("business_profile.phone", e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg bg-white/[0.04] text-white text-[14px] border border-white/[0.08] focus:border-white/30 outline-none"
                        placeholder="+39 333 1234567"
                        data-testid="input-phone"
                    />
                </Field>
            </Section>

            {error && <div className="mt-4 p-3 rounded-xl text-[12px]" style={{ background: "rgba(255,26,92,0.1)", color: "#FF7AA3", border: "1px solid rgba(255,26,92,0.3)" }}>{error}</div>}

            <div className="mt-8 flex items-center gap-3">
                <button type="button" onClick={save} disabled={saving}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white text-[13.5px] transition hover:scale-[1.02] disabled:opacity-60"
                    style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)", fontWeight: 500 }}
                    data-testid="save-customize">
                    {saving ? <Loader size={13} className="animate-spin" /> : <Save size={13} />}
                    {saving ? "Salvataggio…" : "Salva personalizzazione"}
                </button>
                {saved && (
                    <span className="inline-flex items-center gap-1.5 text-[12.5px] text-[#86EFAC]">
                        <Sparkles size={12} /> salvato — Aria si aggiorna in tempo reale
                    </span>
                )}
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="mb-8">
            <div className="text-[10.5px] uppercase tracking-[0.28em] text-white/45 mb-4" style={{ fontFamily: "JetBrains Mono, monospace" }}>{title}</div>
            <div className="space-y-5">{children}</div>
        </section>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="block text-white text-[13px] mb-1.5" style={{ fontWeight: 500 }}>{label}</label>
            {hint && <div className="text-white/45 text-[11.5px] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>{hint}</div>}
            {children}
        </div>
    );
}
